import { dynamicBaseQuery } from "@/api/DynamicBaseQuery";
import { APIEndpoints } from "@/constants/apiEndpoints";
import type {
  IApiGeneralParams,
  IApiPaginatedResponse,
  IApiResponse,
} from "@/types";
import type {
  CreateTeamRolePayload,
  IAssignTeamMembersPayload,
  ICreateTeamResponse,
  ISingleTeam,
  ISingleTeamRole,
  ITeamStats,
} from "@/types/(company)/teams.interface";
import type { ITeamDetailsWithMembers } from "@/types/(my-space)/teams.interface";
import { createApi } from "@reduxjs/toolkit/query/react";

export const companyTeamsApi = createApi({
  reducerPath: "companyTeamsApi",
  baseQuery: dynamicBaseQuery,
  tagTypes: [
    "GetAllTeams",
    "GetTeamRoles",
    "GetTeamRoleById",
    "GetTeamStats",
    "GetTeamMembers",
    "GetTeamDetails",
  ],
  endpoints: (builder) => ({
    // Team endpoints
    getAllTeams: builder.query<
      IApiPaginatedResponse<ISingleTeam[]>,
      {
        search?: string;
        page?: number;
        limit?: number;
        pageSize?: number;
        branchId?: number;
        createdFrom?: string;
        createdTo?: string;
        updatedFrom?: string;
        updatedTo?: string;
      }
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Teams.GetTeams,
        params,
      }),
      providesTags: ["GetAllTeams"],
    }),
    getTeams: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleTeam[]>,
      { limit: number; search?: string; branchId?: number },
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { page, totalPages } = result.meta;
          return page < totalPages ? page + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: APIEndpoints.Company.Teams.GetTeams,
        method: "GET",
        params: {
          limit: queryArg.limit,
          page: pageParam,
          search: queryArg.search,
          ...(queryArg.branchId ? { branchId: queryArg.branchId } : {}),
        },
      }),
    }),
    createTeam: builder.mutation<IApiResponse<ICreateTeamResponse>, FormData>({
      query: (body) => ({
        url: APIEndpoints.Company.Teams.CreateTeam,
        method: "POST",
        body,
      }),
      invalidatesTags: ["GetAllTeams"],
    }),
    updateTeam: builder.mutation<
      IApiResponse<ICreateTeamResponse>,
      { teamId: string; payload: FormData }
    >({
      query: ({ teamId, payload }) => ({
        url: APIEndpoints.Company.Teams.UpdateTeam(teamId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetAllTeams"],
    }),
    companyGetTeamDetails: builder.query<
      IApiResponse<ITeamDetailsWithMembers>,
      { teamId: number }
    >({
      query: ({ teamId }) => ({
        url: APIEndpoints.Company.Teams.GetTeamDetailsByTeamId(teamId),
      }),
      providesTags: ["GetTeamDetails"],
    }),
    deleteTeam: builder.mutation<
      IApiResponse<ICreateTeamResponse>,
      { teamId: string }
    >({
      query: ({ teamId }) => ({
        url: APIEndpoints.Company.Teams.DeleteTeam(teamId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetAllTeams"],
    }),
    assignTeamMembers: builder.mutation<
      IApiResponse<void>,
      {
        teamId: string;
        payload: IAssignTeamMembersPayload;
        isUpdating?: boolean;
      }
    >({
      query: ({ teamId, payload, isUpdating = false }) => ({
        url: APIEndpoints.Company.Teams.AssignTeamMembers(teamId),
        method: isUpdating ? "PUT" : "POST",
        body: payload,
      }),
      invalidatesTags: ["GetAllTeams"],
    }),
    companyGetTeamMembers: builder.query<
      IApiResponse<ITeamDetailsWithMembers>,
      { teamId: number }
    >({
      query: ({ teamId }) => ({
        url: APIEndpoints.Company.Teams.GetTeamDetailsAndMembers(teamId),
      }),
      providesTags: ["GetTeamMembers"],
    }),
    // Teams roles endpoints (settings)
    getTeamRoles: builder.query<
      IApiPaginatedResponse<ISingleTeamRole[]>,
      IApiGeneralParams
    >({
      query: (params) => ({
        url: APIEndpoints.Company.Teams.GetTeamRoles,
        params,
      }),
      providesTags: ["GetTeamRoles"],
    }),
    getTeamRolesQuery: builder.infiniteQuery<
      IApiPaginatedResponse<ISingleTeamRole[]>,
      IApiGeneralParams,
      number
    >({
      infiniteQueryOptions: {
        initialPageParam: 1,
        getNextPageParam: (result) => {
          if (!result?.meta) return undefined;
          const { currentPage, totalPages } = result.meta;
          return currentPage < totalPages ? currentPage + 1 : undefined;
        },
      },
      query: ({ queryArg, pageParam }) => ({
        url: APIEndpoints.Company.Teams.GetTeamRoles,
        method: "GET",
        params: {
          ...queryArg,
          page: pageParam,
        },
      }),
      providesTags: ["GetTeamRoles"],
    }),
    getTeamRoleById: builder.query<
      IApiResponse<ISingleTeamRole>,
      { roleId: string }
    >({
      query: ({ roleId }) => ({
        url: APIEndpoints.Company.Teams.GetTeamRoleById(roleId),
      }),
      providesTags: ["GetTeamRoleById"],
    }),
    createTeamRole: builder.mutation<IApiResponse<void>, CreateTeamRolePayload>(
      {
        query: (body) => ({
          url: APIEndpoints.Company.Teams.CreateTeamRole,
          method: "POST",
          body,
        }),
        invalidatesTags: ["GetTeamRoles"],
      },
    ),
    updateTeamRole: builder.mutation<
      IApiResponse<void>,
      { roleId: string; payload: CreateTeamRolePayload }
    >({
      query: ({ roleId, payload }) => ({
        url: APIEndpoints.Company.Teams.UpdateTeamRole(roleId),
        method: "PUT",
        body: payload,
      }),
      invalidatesTags: ["GetTeamRoles", "GetTeamRoleById"],
    }),
    deleteTeamRole: builder.mutation<
      IApiResponse<ISingleTeamRole>,
      { roleId: string }
    >({
      query: ({ roleId }) => ({
        url: APIEndpoints.Company.Teams.DeleteTeamRole(roleId),
        method: "DELETE",
      }),
      invalidatesTags: ["GetTeamRoles"],
    }),
    companyGetTeamStats: builder.query<IApiResponse<ITeamStats>, void>({
      query: () => ({
        url: APIEndpoints.Company.Teams.GetTeamStats,
      }),
      providesTags: ["GetTeamStats"],
    }),
  }),
});

export const {
  useGetTeamRolesQuery,
  useGetTeamRolesQueryInfiniteQuery,
  useGetTeamRoleByIdQuery,
  useCreateTeamRoleMutation,
  useUpdateTeamRoleMutation,
  useDeleteTeamRoleMutation,
  useCompanyGetTeamStatsQuery,
  useCreateTeamMutation,
  useAssignTeamMembersMutation,
  useGetAllTeamsQuery,
  useDeleteTeamMutation,
  useCompanyGetTeamMembersQuery,
  useCompanyGetTeamDetailsQuery,
  useUpdateTeamMutation,
  useGetTeamsInfiniteQuery,
} = companyTeamsApi;
